import React from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row } from 'react-bootstrap';
import Select from 'react-select';
import { useDarkMode } from 'Hooks';
import { getReactSelectStyles } from 'Helpers/UiHelper';

/**
 * Component for managing CNX/MOD costs on a flight
 *
 * This component displays a checkbox to enable CNX/MOD cost tracking,
 * and when enabled, shows fields for Cost, Who (responsible party),
 * and Reason for the cancellation/modification.
 *
 * @param {Object} props - Component props
 * @param {Object} props.currentSelectedFlight - Current flight data
 * @param {Function} props.setCurrentSelectedFlight - Update flight data
 * @param {Object} props.validationFlightFormErrors - Validation errors
 * @param {Function} props.setValidationFlightFormErrors - Set validation errors
 * @param {Array} props.whoOptions - Dropdown options for "Who"
 * @param {Array} props.reasonOptions - Dropdown options for "Reason"
 * @param {boolean} props.isDisabled - Whether fields are disabled
 */
const CNXModCostSection = ({
  currentSelectedFlight,
  setCurrentSelectedFlight,
  validationFlightFormErrors,
  setValidationFlightFormErrors,
  whoOptions = [],
  reasonOptions = [],
  isDisabled,
}) => {
  const { darkMode } = useDarkMode();

  // Helper to safely get number value for display
  const safeNumberValue = (val) => (val === undefined || val === null || val === '' ? '' : val);

  // Get the current CNX/MOD cost entry (first entry in array, or empty object)
  const currentCnxModCost = currentSelectedFlight?.cnxModCosts?.[0] || {};

  // Check if CNX/MOD cost is enabled (checkbox is checked)
  const cnxModEnabled = currentSelectedFlight?.cnxModCosts?.length > 0;

  // Handle checkbox toggle
  const handleCheckboxChange = (e) => {
    setValidationFlightFormErrors({});
    if (e.target.checked) {
      // Enable CNX/MOD cost - create empty entry
      setCurrentSelectedFlight({
        ...currentSelectedFlight,
        cnxModCosts: [
          {
            cost: '',
            who: '',
            reason: '',
          },
        ],
      });
    } else {
      // Disable CNX/MOD cost - clear the array
      setCurrentSelectedFlight({
        ...currentSelectedFlight,
        cnxModCosts: [],
      });
    }
  };

  // Handle cost field change
  const handleCostChange = (e) => {
    setValidationFlightFormErrors({});
    const newCost = e.target.value;
    setCurrentSelectedFlight({
      ...currentSelectedFlight,
      cnxModCosts: [
        {
          ...currentCnxModCost,
          cost: newCost,
        },
      ],
    });
  };

  // Handle Who dropdown change
  const handleWhoChange = (selectedOption) => {
    setValidationFlightFormErrors({});
    setCurrentSelectedFlight({
      ...currentSelectedFlight,
      cnxModCosts: [
        {
          ...currentCnxModCost,
          who: selectedOption?.value || '',
        },
      ],
    });
  };

  // Handle Reason dropdown change
  const handleReasonChange = (selectedOption) => {
    setValidationFlightFormErrors({});
    setCurrentSelectedFlight({
      ...currentSelectedFlight,
      cnxModCosts: [
        {
          ...currentCnxModCost,
          reason: selectedOption?.value || '',
        },
      ],
    });
  };

  // Map options to react-select format (options already come as {value, label} objects)
  const whoSelectOptions = (whoOptions || []).map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option
  );

  const reasonSelectOptions = (reasonOptions || []).map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option
  );

  // Find current selected values for dropdowns
  const selectedWho = whoSelectOptions.find((opt) => opt.value === currentCnxModCost.who) || null;
  const selectedReason = reasonSelectOptions.find((opt) => opt.value === currentCnxModCost.reason) || null;

  return (
    <>
      <hr />
      <Row>
        <Form.Group as={Col} xs={12}>
          <Form.Check
            type="checkbox"
            id="cnxModCostCheckbox"
            label="CNX/MOD Cost"
            checked={cnxModEnabled}
            onChange={handleCheckboxChange}
            disabled={isDisabled}
          />
        </Form.Group>
      </Row>
      {cnxModEnabled && (
        <Row className="mt-3">
          <Form.Group as={Col}>
            <Form.Label>Cost</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              value={safeNumberValue(currentCnxModCost.cost)}
              onChange={handleCostChange}
              placeholder="0.00"
              disabled={isDisabled}
              isInvalid={!!validationFlightFormErrors.cnxModCost}
            />
            <Form.Control.Feedback type="invalid">{validationFlightFormErrors.cnxModCost}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Who</Form.Label>
            <Select
              isDisabled={isDisabled}
              options={whoSelectOptions}
              isSearchable
              isClearable
              value={selectedWho}
              onChange={handleWhoChange}
              styles={getReactSelectStyles(darkMode)}
              placeholder="Select who..."
            />
            {validationFlightFormErrors.cnxModWho && (
              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                {validationFlightFormErrors.cnxModWho}
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Reason</Form.Label>
            <Select
              isDisabled={isDisabled}
              options={reasonSelectOptions}
              isSearchable
              isClearable
              value={selectedReason}
              onChange={handleReasonChange}
              styles={getReactSelectStyles(darkMode)}
              placeholder="Select reason..."
            />
            {validationFlightFormErrors.cnxModReason && (
              <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                {validationFlightFormErrors.cnxModReason}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Row>
      )}
    </>
  );
};

CNXModCostSection.propTypes = {
  currentSelectedFlight: PropTypes.object.isRequired,
  setCurrentSelectedFlight: PropTypes.func.isRequired,
  validationFlightFormErrors: PropTypes.object.isRequired,
  setValidationFlightFormErrors: PropTypes.func.isRequired,
  whoOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  reasonOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ),
  isDisabled: PropTypes.bool.isRequired,
};

export default CNXModCostSection;
